import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import ReportMarkdown from "../components/ReportMarkdown";
import {
  Button,
  Input,
  Modal,
  ModalContent,
  Title,
} from "../components/StyledComponents";
import { fetchReport, updateSurvey } from "../utils/fetchReport";
import { onlyDigits } from "../utils/cnpj";
import { formatCelular, formatCelularDisplay, formatCnpj } from "../utils/inputMasks";

const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(
    214deg,
    rgba(8, 77, 110, 0.08) 0%,
    rgba(158, 36, 128, 0.08) 100%
  );
`;

const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 100;
  width: 100%;
  background-color: #f7f2fa;
  box-shadow: 0 2px 12px rgba(8, 77, 110, 0.06);
`;

const HeaderInner = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  padding: 12px 20px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 10px 16px;
    gap: 10px;
  }
`;

const HeaderLogoLink = styled.a`
  display: inline-flex;
  flex-shrink: 0;
  cursor: pointer;
`;

const HeaderLogo = styled.img`
  width: 50px;
  height: auto;
  display: block;
`;

const HeaderTitles = styled.div`
  text-align: center;
  flex: 1;
`;

const HeaderTitle = styled.h1`
  font-family: "Oxanium", sans-serif;
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.25;
  margin: 0;
  color: #084d6e;

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const HeaderSubtitle = styled.h4`
  font-family: "Oxanium", sans-serif;
  font-size: 1.05rem;
  font-weight: 500;
  line-height: 1.4;
  margin: 6px 0 0;
  color: #9e2480;

  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

function ReportHeader() {
  return (
    <Header>
      <HeaderInner>
        <HeaderLogoLink
          href="https://diverti.io/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Abrir site da Diverti"
        >
          <HeaderLogo src="/logo_diverti.svg" alt="Diverti" />
        </HeaderLogoLink>
        <HeaderTitles>
          <HeaderTitle>Autodiagnostico da Gestão de Pessoas e Cultura</HeaderTitle>
          <HeaderSubtitle>
            PeopleOps - Cultura, Jornada do Colaborador, Dados e Automação
          </HeaderSubtitle>
        </HeaderTitles>
      </HeaderInner>
    </Header>
  );
}

const Content = styled.main`
  max-width: 960px;
  margin: 0 auto;
  padding: 1.5rem 1.25rem 7rem;
`;

const SectionCard = styled.section`
  background: #fff;
  border-radius: 12px;
  padding: 1.5rem 1.75rem;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
  margin-bottom: 1.25rem;

  @media (max-width: 768px) {
    padding: 1.15rem 1rem;
    border-radius: 10px;
  }
`;

const SectionTitle = styled.h2`
  font-family: "Oxanium", sans-serif;
  font-size: 1.2rem;
  font-weight: 700;
  color: #084d6e;
  margin: 0 0 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &::after {
    content: "";
    flex: 1;
    height: 2px;
    background: linear-gradient(90deg, rgba(158, 36, 128, 0.35), transparent);
    border-radius: 2px;
  }
`;

const BudgetText = styled.div`
  font-family: "Oxanium", sans-serif;
  font-size: 0.95rem;
  line-height: 1.8;
  color: #3d4f5c;
  white-space: pre-line;
  background: linear-gradient(
    135deg,
    rgba(8, 77, 110, 0.04) 0%,
    rgba(158, 36, 128, 0.06) 100%
  );
  border-left: 4px solid #9e2480;
  border-radius: 0 8px 8px 0;
  padding: 1rem 1.15rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1.5rem;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
  max-width: 520px;
  margin: 2rem auto;
`;

const EmptyTitle = styled.h2`
  font-family: "Oxanium", sans-serif;
  font-size: 1.35rem;
  font-weight: 700;
  color: #084d6e;
  margin: 0 0 0.75rem;
`;

const EmptyMessage = styled.p`
  font-size: 1rem;
  color: #5a6b78;
  margin: 0;
`;

const LoadingState = styled(EmptyState)`
  padding: 2.5rem 1.5rem;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(158, 36, 128, 0.2);
  border-top-color: #9e2480;
  border-radius: 50%;
  margin: 0 auto 1rem;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const Footer = styled.footer`
  text-align: center;
  padding: 0 1.25rem 1.5rem;
  font-size: 0.85rem;
  color: #999;
`;

const ActionBar = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 200;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(8px);
  box-shadow: 0 -4px 20px rgba(8, 77, 110, 0.12);
  border-top: 1px solid rgba(8, 77, 110, 0.08);
`;

const ActionBarInner = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: 0.85rem 1.25rem;
  display: flex;
  gap: 0.85rem;
  justify-content: center;

  @media (max-width: 600px) {
    flex-direction: column;
    padding: 0.75rem 1rem;
  }
`;

const ActionButton = styled.button`
  font-family: "Oxanium", sans-serif;
  font-size: 1rem;
  font-weight: 600;
  padding: 0.85rem 1.75rem;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  flex: ${(props) => (props.$solo ? "0 1 auto" : 1)};
  max-width: 320px;

  ${(props) =>
    props.variant === "primary"
      ? `
    background: linear-gradient(135deg, #084d6e 0%, #9e2480 100%);
    color: #fff;
    box-shadow: 0 6px 18px rgba(158, 36, 128, 0.28);
  `
      : `
    background: #fff;
    color: #084d6e;
    border: 2px solid #084d6e;
  `}

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 22px rgba(8, 77, 110, 0.22);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 600px) {
    max-width: none;
  }
`;

const ModalForm = styled.div`
  text-align: left;
  margin-top: 1.25rem;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: none;
  background: #f0eef2;
  color: #084d6e;
  font-size: 1.1rem;
  font-weight: 700;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover {
    background: #e2dde6;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const FieldGroup = styled.div`
  margin-bottom: 1rem;
`;

const FieldLabel = styled.label`
  display: block;
  font-family: "Oxanium", sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  color: #084d6e;
  margin-bottom: 0.35rem;
`;

const ModalInput = styled(Input)`
  margin-top: 0;
  margin-bottom: 0;
`;

const PositionedModalContent = styled(ModalContent)`
  position: relative;
`;

const ModalActions = styled.div`
  margin-top: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
`;

const ModalErrorMessage = styled.p`
  margin: 0;
  font-size: 0.92rem;
  color: #b42318;
  text-align: center;
`;

const ButtonSpinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(8, 77, 110, 0.2);
  border-top-color: #084d6e;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  flex-shrink: 0;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LiberarButtonContent = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const LiberarButton = styled(Button)`
  width: 100%;
  max-width: 280px;
  border-radius: 999px;
  background: #fff;
  color: #084d6e;
  border: 2px solid #084d6e;
  box-shadow: none;

  &:hover {
    background: #f7f2fa;
    box-shadow: 0 4px 12px rgba(8, 77, 110, 0.15);
  }
`;

const CONSULTANT_SCHEDULE_URL =
  "https://calendar.app.google/t9xjtykVaguVuEdy7";

function openConsultantSchedule() {
  window.open(CONSULTANT_SCHEDULE_URL, "_blank", "noopener,noreferrer");
}

function hasCompleteContactData(celular, cnpj) {
  return onlyDigits(celular).length === 11 && onlyDigits(cnpj).length === 14;
}

function BudgetRequestModal({
  open,
  onClose,
  onSubmit,
  initialCelular = "",
  initialCnpj = "",
}) {
  const [celular, setCelular] = useState("");
  const [cnpj, setCnpj] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    if (open) {
      setCelular(initialCelular ? formatCelularDisplay(initialCelular) : "");
      setCnpj(initialCnpj ? formatCnpj(initialCnpj) : "");
      setSubmitError("");
      setSubmitting(false);
    } else {
      setCelular("");
      setCnpj("");
      setSubmitError("");
      setSubmitting(false);
    }
  }, [open, initialCelular, initialCnpj]);

  if (!open) return null;

  const handleBackdropClick = (event) => {
    if (submitting) return;
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;

    setSubmitting(true);
    setSubmitError("");

    try {
      await onSubmit({ celular, cnpj });
    } catch {
      setSubmitError("Não conseguimos atualizar, tente novamente");
      setSubmitting(false);
    }
  };

  const canSubmit = hasCompleteContactData(celular, cnpj);

  return (
    <Modal onClick={handleBackdropClick}>
      <PositionedModalContent>
        <CloseButton
          type="button"
          onClick={onClose}
          aria-label="Fechar"
          disabled={submitting}
        >
          ✕
        </CloseButton>
        <Title>Pedir Orçamento</Title>
        <ModalForm>
          <FieldGroup>
            <FieldLabel htmlFor="budget-celular">Celular</FieldLabel>
            <ModalInput
              id="budget-celular"
              type="tel"
              inputMode="numeric"
              placeholder="(19)98147-7222"
              value={celular}
              disabled={submitting}
              onChange={(e) => setCelular(formatCelularDisplay(e.target.value))}
            />
          </FieldGroup>

          <FieldGroup>
            <FieldLabel htmlFor="budget-cnpj">CNPJ</FieldLabel>
            <ModalInput
              id="budget-cnpj"
              type="text"
              inputMode="numeric"
              placeholder="00.000.000/0001-91"
              value={cnpj}
              disabled={submitting}
              onChange={(e) => setCnpj(formatCnpj(e.target.value))}
            />
          </FieldGroup>

          <ModalActions>
            {submitError && <ModalErrorMessage>{submitError}</ModalErrorMessage>}
            <LiberarButton
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
            >
              <LiberarButtonContent>
                {submitting && <ButtonSpinner aria-hidden="true" />}
                {submitting ? "Liberando..." : "Liberar"}
              </LiberarButtonContent>
            </LiberarButton>
          </ModalActions>
        </ModalForm>
      </PositionedModalContent>
    </Modal>
  );
}

function ReportPage() {
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code")?.trim() || "";
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(Boolean(code));
  const [error, setError] = useState(null);
  const [budgetModalOpen, setBudgetModalOpen] = useState(false);

  useEffect(() => {
    if (!code) {
      setReport(null);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;

    async function loadReport() {
      setLoading(true);
      setError(null);

      try {
        const data = await fetchReport(code);
        if (!cancelled) {
          setReport(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Não foi possível carregar o diagnóstico.");
          setReport(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadReport();

    return () => {
      cancelled = true;
    };
  }, [code]);

  if (!code) {
    return (
      <PageWrapper>
        <ReportHeader />
        <Content>
          <EmptyState>
            <EmptyTitle>Diagnóstico não encontrado</EmptyTitle>
            <EmptyMessage>Não existe diagnostico registrado</EmptyMessage>
          </EmptyState>
        </Content>
      </PageWrapper>
    );
  }

  if (loading) {
    return (
      <PageWrapper>
        <ReportHeader />
        <Content>
          <LoadingState>
            <LoadingSpinner />
            <EmptyMessage>Carregando diagnóstico...</EmptyMessage>
          </LoadingState>
        </Content>
      </PageWrapper>
    );
  }

  if (error || !report) {
    return (
      <PageWrapper>
        <ReportHeader />
        <Content>
          <EmptyState>
            <EmptyTitle>Diagnóstico não encontrado</EmptyTitle>
            <EmptyMessage>
              {error || "Não existe diagnostico registrado"}
            </EmptyMessage>
          </EmptyState>
        </Content>
      </PageWrapper>
    );
  }

  const budgetAlreadyRequested = hasCompleteContactData(
    report.celular,
    report.cnpj
  );

  const handleBudgetSubmit = async ({ celular, cnpj }) => {
    await updateSurvey({ code, cnpj, celular });
    const updated = await fetchReport(code);
    setReport(updated);
    setBudgetModalOpen(false);
  };

  return (
    <PageWrapper>
      <ReportHeader />

      <Content>
        <SectionCard>
          <SectionTitle>📋 Relatório</SectionTitle>
          {report.output ? (
            <ReportMarkdown content={report.output} />
          ) : (
            <EmptyMessage>Relatório ainda não disponível.</EmptyMessage>
          )}
        </SectionCard>

        {budgetAlreadyRequested && (
          <SectionCard>
            <SectionTitle>💰 Orçamento</SectionTitle>
            {report.budget ? (
              <BudgetText>{report.budget}</BudgetText>
            ) : (
              <EmptyMessage>Orçamento ainda não disponível.</EmptyMessage>
            )}
          </SectionCard>
        )}
      </Content>

      <Footer>Diverti — Gestão de pessoas com dados</Footer>

      <ActionBar>
        <ActionBarInner>
          {!budgetAlreadyRequested && (
            <ActionButton
              variant="secondary"
              type="button"
              onClick={() => setBudgetModalOpen(true)}
            >
              Pedir Orçamento
            </ActionButton>
          )}
          <ActionButton
            variant="secondary"
            type="button"
            $solo={budgetAlreadyRequested}
            onClick={openConsultantSchedule}
          >
            Agendar com Consultor
          </ActionButton>
        </ActionBarInner>
      </ActionBar>

      {!budgetAlreadyRequested && (
        <BudgetRequestModal
          open={budgetModalOpen}
          onClose={() => setBudgetModalOpen(false)}
          onSubmit={handleBudgetSubmit}
          initialCelular={report.celular}
          initialCnpj={report.cnpj}
        />
      )}
    </PageWrapper>
  );
}

export default ReportPage;
